<!--
  BACK TO TOP — the way out of the bottom of a long page.

  The owner's ask, verbatim: reaching the bottom of any page should offer one
  press that puts you back at the top. So this is not a floating widget that
  follows the scroll — those sit over posters and fight the tab bar — it is the
  LAST THING IN THE PAGE FLOW, a centred glass pill you only ever meet by
  arriving at the bottom, which is exactly the moment it is useful. Mounted once
  in `App.svelte` under the routed content, so every browse screen has it and no
  screen mounts its own; the watch screen never sees it because the watch screen
  never mounts the shell's <main> at all.

  IT ONLY EXISTS WHEN THE PAGE ACTUALLY SCROLLS. A page shorter than the glass
  has no "top" to go back to, and a pill that says otherwise is furniture. The
  bar is rendered — not hidden, rendered — only when the document overflows the
  viewport by at least half a screen, re-measured as the lazy rails beneath it
  fill in (a ResizeObserver on <main>, plus the window's own resize).

  THREE WAYS UP, matched to how the app already moves:
   • pointer/keyboard: one smooth scroll to zero, and the button lets go of the
     focus — the next Tab starts from the top, which is where the reader now is;
   • prefers-reduced-motion: the same journey, instantly;
   • television (`html[data-tv='on']`): an instant jump — every scroll the
     remote makes is instant (`dpad.ts`) and a smooth glide would be the one
     thing still moving — and the focus is HANDED to the first real thing at
     the top of the page, because on a TV the focus is the viewer's position
     and leaving it on an offscreen pill would strand them (worse: the dpad's
     keep-in-view correction would dutifully scroll them straight back down).

  A d-pad citizen by geometry: it is a plain focusable button at the foot of the
  document, so Down from the last rail finds it without a single attribute of
  navigation. `data-dpad-chrome` marks it as furniture — reachable, but never
  where the remote's FIRST press lands when a screen appears.

  Selection is the app-wide `:focus-visible` ring (`--focus-ring` luminance,
  never `--accent`) — no rule here draws one of its own.
-->
<script lang="ts">
  import Icon from './Icon.svelte';

  /** True once the document is meaningfully taller than the viewport. */
  let scrolls = $state(false);

  function measure(): void {
    const doc = document.documentElement;
    scrolls = doc.scrollHeight - doc.clientHeight >= doc.clientHeight / 2;
  }

  $effect(() => {
    measure();
    window.addEventListener('resize', measure);
    // Main grows with routed content; body is fixed at 100% viewport height.
    if (typeof ResizeObserver === 'undefined') {
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    const content = document.querySelector('main');
    if (content) ro.observe(content);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  });

  /**
   * The first real thing at the top of the page, for the television's focus to
   * land on. Document order is visual order at the top of every screen this
   * app has, so: the first focusable that is not app furniture
   * (`data-dpad-chrome`), not a non-destination (`data-dpad-skip`), and
   * actually laid out. On Home and the browse screens that is the hero's
   * full-bleed link; on a grid it is the first tile.
   */
  function focusTop(): void {
    const candidates = document.querySelectorAll<HTMLElement>(
      'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
    );
    for (const el of candidates) {
      if (el.closest('[data-dpad-chrome], [data-dpad-skip], [inert]')) continue;
      const box = el.getBoundingClientRect();
      if (box.width <= 0 || box.height <= 0) continue;
      if (box.top >= window.innerHeight) break; // past the first screenful: nothing qualified
      if (box.bottom <= 0) continue;
      el.focus({ preventScroll: true });
      return;
    }
  }

  function toTop(event: MouseEvent): void {
    const tv = document.documentElement.dataset.tv === 'on';
    const reduced =
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({ top: 0, behavior: tv || reduced ? 'instant' : 'smooth' });

    if (tv) {
      // The focus is the viewer. Take it to the top with the page, or the
      // dpad's keep-in-view correction scrolls the page right back here.
      focusTop();
      return;
    }
    // Let go: a focus ring on a pill that is now five screens below the reader
    // is a trap for the next Tab. From <body>, tabbing starts at the top —
    // which is where the reader just asked to be.
    (event.currentTarget as HTMLElement | null)?.blur();
  }
</script>

{#if scrolls}
  <div class="foot">
    <button type="button" class="pill" data-dpad-chrome onclick={toTop}>
      <Icon name="up" size={15} />
      <span>Back to top</span>
    </button>
  </div>
{/if}

<style>
  .foot {
    display: grid;
    place-items: center;
    padding: var(--space-6) max(var(--page-pad), var(--safe-l), var(--safe-r)) var(--space-2);
  }

  /*
   * The house glass, quiet register: the same fill/line/blur set every
   * floating pill in the shell wears, at the dim text tone — this is an exit,
   * not a call to action, and it must not outshine the rails above it.
   */
  .pill {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-height: var(--hit);
    padding-inline: var(--space-5);
    border: 1px solid var(--glass-line);
    border-radius: var(--radius-pill);
    background: var(--glass-fill);
    -webkit-backdrop-filter: var(--glass-blur);
    backdrop-filter: var(--glass-blur);
    box-shadow: var(--glass-shine);
    color: var(--text-dim);
    font-size: var(--text-sm);
    font-weight: var(--weight-semi);
    transition:
      background-color 140ms var(--ease),
      border-color 140ms var(--ease),
      color 140ms var(--ease);
  }
  .pill:hover,
  .pill:focus-visible {
    background: var(--glass-fill-hi);
    border-color: var(--glass-line-hi);
    color: var(--text);
  }
</style>
